/* eslint-disable */
/**
 * ═════════════════════════════════════════════════════════════════════════
 *   Cocoon Nest — PackageEngine (IMMUTABLE)
 *   Iter-299 · Feb 2026 · "Break-the-Glass" isolation of the clinical +
 *   financial brain behind every Package invoice.
 * ═════════════════════════════════════════════════════════════════════════
 *
 *   THIS FILE IS IMMUTABLE.
 *
 *   Rules for future agents / humans (also recorded in SYSTEM_RULES.md):
 *     1. Every function that touches Package IDs, bundling, pro-rata
 *        pricing, discount %, or Shredding-prevention MUST live here.
 *     2. This file MUST NOT be edited or refactored as part of a UI task,
 *        a design pass, an ESLint autofix, a Prettier reformat, or any
 *        "cleanup" pass. If a bug is genuinely inside the engine, open a
 *        dedicated task for it and update SYSTEM_RULES accordingly.
 *     3. Consumers (BranchOrders.jsx, CustomerCheckIn.jsx, any future
 *        checkout / cart / kiosk surface) MUST call the exported
 *        functions rather than re-implementing the math inline. Inline
 *        package math anywhere else in the codebase is a P0 regression.
 *     4. The engine is pure — no React state, no side effects, no
 *        network calls. Callers pass in the data they've already
 *        fetched; the engine returns the canonical item shape the
 *        server persists.
 *
 *   The rendering contract this engine emits is the "Stewart Gallacher
 *   Rule" (Iter-296): every package on every invoice is
 *
 *       [ 1 x kind:"package"  header       (list-price = package price)  ]
 *       [ N x kind:"service"  children     (commission_only=True,
 *                                            unit_price = catalog list,
 *                                            adjustment = "Package discount",
 *                                            discount_type = "percent",
 *                                            discount_value = pct)      ]
 *
 *   Zero children may ever emit `unit_price: 0 + adjustment: null` — that
 *   is the shredded pattern the Iter-296 audit forbade. Preventing that
 *   pattern is the engine's core responsibility.
 *
 *   ── Where the logic was extracted from (git blame anchors) ──────────
 *     - `BranchOrders.jsx` lines 225-386 (svc/pkg branch pricing,
 *       resolveLine, package flow, ad-hoc multi-service flow, single
 *       flow). Extracted verbatim to preserve behaviour byte-for-byte.
 *     - `CustomerCheckIn.jsx` was inspected — it contains NO package
 *       logic (guest check-in never bills; the invoice is created
 *       later by the front desk from BranchOrders). The `.jsx`
 *       extension and this comment are preserved so the file appears
 *       in the same import surface both consumers reach for.
 *   ─────────────────────────────────────────────────────────────────────
 */

// ── Substitution helper ──────────────────────────────────────────────
// A package item may declare `substitutions: [service_id, …]` in the
// Brand Catalog wizard — the operator can then swap the delivered
// service on that step (booker.com-style) at booking time. The chosen
// substitute is persisted as the appointment's `service_id`; the
// bundle's `package_id` stays intact so BillingEngine + this engine
// still treat the visit as ONE bundled item. Returns the ordered list
// of allowed services (original first, substitutes after) filtered to
// what the caller's `services` catalog actually contains, or `null`
// when the item has no substitutions (the caller then skips the
// dropdown and renders the plain service name).
//
//   pkg        — the package catalog doc (needs `items[]`)
//   itemIndex  — 0-based index into `pkg.items`
//   services   — the full services catalog (usually the modal's prop)
//
//   → returns `[Service, Service, …]` or `null`

export const substitutionOptionsForItem = (pkg, itemIndex, services) => {
  const item = pkg?.items?.[itemIndex];
  if (!item) return null;
  const subs = Array.isArray(item.substitutions)
    ? item.substitutions.filter(Boolean)
    : [];
  if (subs.length === 0) return null;
  const ids = [item.service_id, ...subs].filter(Boolean);
  const opts = ids
    .map((sid) => (services || []).find((s) => s.id === sid))
    .filter(Boolean);
  // Deduplicate in case a substitute list accidentally includes the
  // original id — the picker MUST NOT render the same service twice.
  const seen = new Set();
  return opts.filter((s) => {
    if (seen.has(s.id)) return false;
    seen.add(s.id);
    return true;
  });
};

// ── Branch-aware pricing helpers ─────────────────────────────────────
// The "same for all locations" flag on a Service/Package doc means the
// top-level `pricing.price` wins even if the doc still carries a stale
// `per_branch` override from a prior configuration. Historical carriers
// (`svc.price`, `pkg.price`) are kept as a final fallback so legacy
// documents render.

export const svcPriceForBranch = (svc, branchId) => {
  const p = svc?.pricing || {};
  if (p.same_for_all === false) {
    const bp = p.per_branch?.[branchId]?.price;
    if (bp !== undefined && bp !== null) return Number(bp) || 0;
  }
  return Number(p.price ?? svc?.price ?? 0) || 0;
};

export const pkgPriceForBranch = (pkg, branchId) => {
  const p = pkg?.pricing || {};
  if (p.same_for_all === false) {
    const bp = p.per_branch?.[branchId]?.price;
    if (bp !== undefined && bp !== null) return Number(bp) || 0;
  }
  return Number(p.price ?? pkg?.price ?? 0) || 0;
};

// ── Single-appointment line resolver ────────────────────────────────
// When a Service record can be matched by id, the current catalog
// price wins (respecting `same_for_all`); otherwise the appointment's
// stored snapshot price is used so historical bookings still render.
// The `looksLikeSameService` guard prevents a Service whose NAME
// changed since booking from silently repricing an old appointment.

export const resolveApptLine = (a, services, branchId) => {
  const svc = services.find((x) => x.id === a.service_id);
  const snapshotName = (a.service_name || "").trim();
  const looksLikeSameService =
    svc &&
    (!snapshotName ||
      (svc.name || "").trim().toLowerCase() === snapshotName.toLowerCase());
  let name;
  let unit_price;
  let refId;
  if (looksLikeSameService) {
    name = svc.name;
    unit_price = svcPriceForBranch(svc, branchId);
    refId = svc.id;
  } else {
    name = snapshotName || (svc && svc.name) || "Service";
    unit_price =
      Number(
        a.price ??
          a.service_price ??
          (svc ? svcPriceForBranch(svc, branchId) : 0),
      ) || 0;
    refId = a.service_id;
  }
  return {
    kind: "service",
    ref_id: refId,
    name,
    quantity: 1,
    unit_price,
    appointment_id: a.id,
    staff_ids: a.staff_id ? [a.staff_id] : [],
  };
};

// ── Pro-rata package discount %  ────────────────────────────────────
// The discount % is applied only to services with a non-zero list price
// so complimentary items ("15 Minutes Free Doctor Consultation") stay at
// 0 in the commission breakdown and don't distort the discount rate.
// Returns 0 when the package is FREE, when siblings sum to 0, or when
// the package price is >= the list total (no discount to apply).

export const computePackageDiscountPct = (listPaidTotal, packageBranchPrice) => {
  if (!(listPaidTotal > 0)) return 0;
  if (!(packageBranchPrice < listPaidTotal)) return 0;
  return ((listPaidTotal - packageBranchPrice) / listPaidTotal) * 100;
};

// ── Shredding-prevention guard ──────────────────────────────────────
// Refuses to emit a `commission_only` child with `unit_price: 0` and
// `adjustment: null`. That combination is the "shredded" pattern the
// Iter-296 audit forbade — every paid child MUST carry either a real
// list price or an explicit Package Discount adjustment.

const assertNotShredded = (child) => {
  const price = Number(child?.unit_price || 0);
  const hasAdj = !!child?.adjustment;
  if (price === 0 && !hasAdj && child?.commission_only === true) {
    // eslint-disable-next-line no-console
    console.error(
      "[PackageEngine] Refusing to emit shredded child (unit_price: 0 + adjustment: null). This is a P0 regression. Item:",
      child,
    );
  }
  return child;
};

// ── Master builder ──────────────────────────────────────────────────
// Consumes an appointment (+ optional siblings + optional package doc)
// and returns the canonical items array + package metadata to persist
// on the draft order. All three shapes (package, ad-hoc multi-service,
// single) are handled in one entry point so the caller cannot
// accidentally pick the wrong flow.
//
//   input:
//     appt          — the primary appointment (required)
//     groupAppts    — sibling appointments sharing link_group_id, already
//                     filtered (status !== "cancelled") and chronologically
//                     sorted; may be [] / null when not applicable
//     pkg           — the Package catalog doc when `appt.package_id`
//                     matches; may be null
//     services      — the full services catalog (needed for name / price
//                     canonicalisation)
//     branchId      — the current branch id (used by svcPriceForBranch /
//                     pkgPriceForBranch)
//
//   returns:
//     { items, packageId, packageName, orderNote }

export const buildOrderItemsForAppointment = ({
  appt,
  groupAppts,
  pkg,
  services,
  branchId,
}) => {
  const items = [];
  let orderNote = "";
  let packageId = null;
  let packageName = null;

  const linkGroupId = appt?.link_group_id || null;
  const pkgId = appt?.package_id || null;
  const group = Array.isArray(groupAppts) ? groupAppts : [];

  // ---- Package flow: link_group_id + package_id both present ---------
  if (linkGroupId && pkgId) {
    const packageBranchPrice = pkg ? pkgPriceForBranch(pkg, branchId) : 0;

    if (group.length && pkg && packageBranchPrice > 0) {
      // Only siblings that belong to the package share its discount —
      // ad-hoc add-ons booked into the same visit bill at list price.
      const pkgGroup = group.filter((a) => a.package_id);
      const extras = group.filter((a) => !a.package_id);
      const svcLines = pkgGroup.map((a) => resolveApptLine(a, services, branchId));
      const paidTotal = svcLines.reduce(
        (s, l) => s + Number(l.unit_price || 0),
        0,
      );
      const discountPct = computePackageDiscountPct(paidTotal, packageBranchPrice);
      packageId = pkgId;
      packageName = pkg.name || "";
      orderNote = packageName ? `Package: ${packageName}` : "";

      // ── Line 1: the Package itself. This is the ONLY billable line
      // — its price equals the configured package price so the invoice
      // total lands on the exact amount the customer pays.
      items.push({
        kind: "package",
        ref_id: pkg.id,
        name: pkg.name,
        quantity: 1,
        unit_price: packageBranchPrice,
        // No appointment_id on the master row — the sibling
        // appointments live on the commission-only rows below and
        // that's where /orders/checkout will settle each booking.
        staff_ids: [],
      });

      // ── Lines 2..N: per-service commission breakdown. Each row
      // shows the employee who did the work, the service's normal
      // list price, the package discount %, and the discounted
      // "commission portion". `commission_only: true` makes these
      // rows contribute 0 to the invoice total.
      for (const l of svcLines) {
        items.push(
          assertNotShredded({
            ...l,
            commission_only: true,
            adjustment:
              l.unit_price > 0 && discountPct > 0
                ? {
                    kind: "special",
                    special_id: null,
                    discount_type: "percent",
                    discount_value: Number(discountPct.toFixed(6)),
                    // Intentionally no package name here — the row itself is
                    // already visually nested under the Package master row,
                    // and staff don't want the package name repeated in the
                    // Adjustment column / printed receipt / email invoice.
                    name: "Package discount",
                  }
                : null,
          }),
        );
      }
      // Ad-hoc services added onto this visit (no package_id) bill as
      // regular full-price lines on the same invoice.
      for (const ex of extras) items.push(resolveApptLine(ex, services, branchId));
      return { items, packageId, packageName, orderNote };
    }

    // Fallback: package meta missing or price = 0 → seed the group
    // without any discount so the operator can still see the whole
    // visit on one invoice.
    const fallbackItems =
      group.length > 0
        ? group.map((a) => resolveApptLine(a, services, branchId))
        : [resolveApptLine(appt, services, branchId)];
    return {
      items: fallbackItems,
      packageId: pkgId,
      packageName: pkg?.name || null,
      orderNote: pkg?.name ? `Package: ${pkg.name}` : "",
    };
  }

  // ---- Ad-hoc multi-service flow: link_group_id present, no package_id
  // The customer booked N different services in one visit via "+ Add
  // Another Service". Seed the invoice with EVERY sibling appointment as
  // its own regular line — no package banner, no discount.
  if (linkGroupId && !pkgId) {
    const rows =
      group.length > 0
        ? group.map((a) => resolveApptLine(a, services, branchId))
        : [resolveApptLine(appt, services, branchId)];
    return { items: rows, packageId: null, packageName: null, orderNote: "" };
  }

  // ---- Single-service flow (unchanged) -------------------------------
  return {
    items: [resolveApptLine(appt, services, branchId)],
    packageId: null,
    packageName: null,
    orderNote: "",
  };
};
